import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@designs-by-maryam/ui/components/card";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Globe, Quote, Sparkles, Users } from "lucide-react";
import { motion } from "motion/react";
import { getStaggerDelay, useSafeMotion } from "@/lib/motion";
import type { PhilosophyItem } from "./about-content";

const iconMap = {
	Sparkles,
	Globe,
	Users,
} as const;

type PhilosophySectionProps = {
	items: PhilosophyItem[] | readonly PhilosophyItem[];
	className?: string;
};

/**
 * PhilosophySection Component
 *
 * Displays three design philosophy cards with icons, titles, and descriptions.
 *
 * Features:
 * - Responsive grid (3 columns desktop, 1 column mobile)
 * - Lucide icons for each philosophy
 * - Staggered entrance animations
 * - Pull quote decorations
 * - Background pattern overlay
 * - Enhanced hover effects with depth
 */
export function PhilosophySection({
	items,
	className,
}: PhilosophySectionProps) {
	const { shouldReduce, slideUp, scaleIn } = useSafeMotion();

	return (
		<section
			aria-labelledby="philosophy-heading"
			className={cn(
				"relative overflow-hidden bg-muted/30 py-16 md:py-24",
				className,
			)}
		>
			{/* Decorative Background Pattern */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.02]"
				style={{
					backgroundImage:
						"radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)",
					backgroundSize: "32px 32px",
				}}
			/>

			{/* Subtle Gradient Accents */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 overflow-hidden"
			>
				<div className="-top-24 -left-24 absolute h-48 w-48 rounded-full bg-primary/5 blur-xl" />
				<div className="-bottom-24 -right-24 absolute h-48 w-48 rounded-full bg-purple-500/3 blur-xl" />
			</div>

			<div className="container relative mx-auto px-4 md:px-6">
				{/* Section Header */}
				<motion.div
					animate={slideUp.animate}
					className="mb-12 text-center"
					initial={slideUp.initial}
					transition={slideUp.transition}
				>
					<div className="mb-4 flex justify-center">
						<Quote className="h-8 w-8 text-primary/20" />
					</div>
					<h2
						className="font-bold text-3xl text-foreground tracking-tight md:text-4xl"
						id="philosophy-heading"
					>
						Design Philosophy
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
						The principles that guide every project I undertake
					</p>
				</motion.div>

				{/* Philosophy Cards Grid */}
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{items.map((item, index) => {
						const IconComponent = iconMap[item.icon];
						return (
							<motion.div
								animate={scaleIn.animate}
								initial={scaleIn.initial}
								key={item.title}
								transition={{
									duration: shouldReduce ? 0.01 : 0.4,
									delay: getStaggerDelay(index, 0.2, 0.15, shouldReduce),
								}}
							>
								<Card className="group hover:-translate-y-1 relative h-full overflow-hidden border-border/30 bg-card/50 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-primary/30 hover:shadow-xl dark:hover:border-primary/40">
									{/* Decorative corner accent */}
									<div
										aria-hidden="true"
										className="absolute top-0 right-0 h-20 w-20 bg-gradient-to-bl from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
									/>

									{/* Quote decoration */}
									<div
										aria-hidden="true"
										className="absolute top-4 left-4 opacity-5 transition-opacity duration-300 group-hover:opacity-10"
									>
										<Quote className="h-16 w-16 text-primary" />
									</div>

									<CardHeader className="relative pb-2">
										<motion.div
											className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md dark:from-primary/20 dark:to-primary/10 dark:text-primary"
											whileHover={{ rotate: 5 }}
										>
											<IconComponent aria-hidden="true" className="h-7 w-7" />
											{/* Icon glow effect */}
											<div className="-z-10 absolute inset-0 rounded-xl bg-primary/20 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100" />
										</motion.div>
										<CardTitle className="text-xl leading-tight">
											{item.title}
										</CardTitle>
									</CardHeader>
									<CardContent>
										<p className="relative text-muted-foreground leading-relaxed">
											{item.description}
										</p>
									</CardContent>

									{/* Bottom accent line */}
									<div
										aria-hidden="true"
										className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-primary to-primary/80 transition-all duration-300 group-hover:w-full"
									/>
								</Card>
							</motion.div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
